import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Link,
  Checkbox,
  Stack,
  FormControl,
  Divider,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import PasswordIcon from "@mui/icons-material/Password";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { useLoginUserMutation } from "../../services/auth/authApiSlice";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import useLogin from "../../hooks/useLogin";
// import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { useGetCaptchaMutation, useVerifyCaptchaMutation } from "../../services/captcha/captchaApiSlice";
import { Card, CardContent } from "@mui/joy";
import { Refresh } from "@mui/icons-material";
import { useGetFetchUserManualMutation } from "../../services/vendor/VendorApi";

type LoginTypeOption = "vendor" | "employee";
type AuthMethod = "password" | "otp" | null;

interface AuthFormProps { }

const AuthForm: React.FC<AuthFormProps> = () => {
  // const recaptcha = useRef(null);

  const navigate = useNavigate();
  const [isClient, setIsClient] = useState(false);
  const [loginType, setLoginType] = useState<LoginTypeOption>("vendor");
  const [identifier, setIdentifier] = useState<string>("");
  const [showAuthMethodSelector, setShowAuthMethodSelector] = useState<boolean>(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>(null);
  const [password, setPassword] = useState<string>("");
  const [captchaValue, setCaptchaValue] = useState<string>("");
  const [isCaptchaVerified, setIsCaptchaVerified] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [cookies, setCookie, removeCookie] = useCookies(["authToken", "userData"]);

  const sdate1 = new Date(process.env.REACT_APP_START_TIME);
  const edate2 = new Date(process.env.REACT_APP_END_TIME);
  const cdate2 = new Date();
  sdate1.setHours(0, 0, 0, 0);
  edate2.setHours(0, 0, 0, 0);
  cdate2.setHours(0, 0, 0, 0);

  var isEnableMeetingLink = (cdate2 >= sdate1 && cdate2 <= edate2);

  const { handleSSOLogin } = useLogin();

  const [getUserManual] = useGetFetchUserManualMutation();

  const [loginUser, { data: loginResponse, error: loginApiError, isLoading, isError, isSuccess, reset }] = useLoginUserMutation();

  const [getCaptcha, { data: captchaResponse, error: captchaApiError, isLoading: isCaptchaLoading }] = useGetCaptchaMutation();
  const [verifyCaptcha, { data: captchaVerificationResponse, error: captchaVerificationApiError, isLoading: isCaptchaVerifyLoading }] = useVerifyCaptchaMutation();

  useEffect(() => {
    setIsClient(true);
  }, []);



  const openUserManual = async () => {
    try {
      const response = await getUserManual(5);
      if (response?.data?.success) {

        const { data, file_name, mime_type, presigned_url } = response?.data?.file;
        window.open(presigned_url, "_blank");

        // Clean up the URL after a short delay to allow the browser to load it
        setTimeout(() => {
          URL.revokeObjectURL(presigned_url);
        }, 1000);
      }
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  }

  if (!isClient) return null;

  const handleLoginTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newLoginType = event.target.value as LoginTypeOption; // Assert here as well
    setLoginType(newLoginType);
    setIdentifier("");
    setShowAuthMethodSelector(false);
    setAuthMethod(null);
    setPassword("");
    setOtp("");
    reset(); // Reset mutation state if login type changes
  };

  const handleIdentifierChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setIdentifier(value);
    setShowAuthMethodSelector(value.length > 0);
    if (value.length === 0) {
      setAuthMethod(null);
    }
    if (isError || isSuccess) reset(); // Reset mutation state if identifier changes after a submission
  };

  const selectAuthMethod = async (method: AuthMethod) => {
    try {
      await getCaptcha();
      setAuthMethod(method);
      setPassword("");
      setOtp("");
      if (isError || isSuccess) reset(); // Reset mutation state
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  };

  const handleCaptchaRefetch = async () => {
    try {
      await getCaptcha();
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  };

  // Handle password and OTP input changes and reset mutation state
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (isError || isSuccess) reset();
  };

  const handleOtpChange = (event: ChangeEvent<HTMLInputElement>) => {
    if ('otp' === event.target.name && event.target.value.length > 6) {
      return;
    }
    setOtp(event.target.value);
    if (isError || isSuccess) reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>, force_login: boolean = false) => {
    event?.preventDefault();
    // const captchaValue = recaptcha.current.getValue();
    // if (!captchaValue) {
    //   toast.error("Please verify the reCAPTCHA!");
    //   return;
    // }

    let reqPayload: any = {
      remember: rememberMe,
      login_type: loginType,
      auth_method: authMethod,
      force_login,
      // captcha: captchaValue,
    };

    if (loginType === "vendor") {
      let removeLeadingzero = identifier.replace(/^0+/, "");
      reqPayload.vendor_id = "00" + removeLeadingzero;
    } else if (loginType === "employee") {
      reqPayload.vendor_id = identifier; // Assuming employee uses 'user_id'
    }

    if (authMethod === "password") {
      reqPayload.password = password;
    } else if (authMethod === "otp") {
      reqPayload.otp = otp;
    }

    try {
      const result = await loginUser(reqPayload).unwrap();

      // 1. Set the token first
      if (result.token) {
        localStorage.setItem("authToken", result.token);
      }

      // Use optional chaining (?.) for safer property access.
      // This prevents errors if 'user_data' itself is missing.
      const userData = result?.user_data;

      if (userData) {
        document.body.classList.add("Login");
        // 2. Use an if/else if structure to prevent overwriting the role
        if (userData.vendor_id) {
          localStorage.setItem("userRole", "vendor");
          // Ensure the value being set is a string
          localStorage.setItem("vendor_code", String(userData?.vendor_id));
          localStorage.setItem("employee_id", String(userData?.vendor_id));
          localStorage.setItem("vendor_name", String(userData?.username));
        } else if (userData.employee_id) {
          localStorage.setItem("userRole", "employee");
          if (userData?.employee_id) localStorage.setItem("employee_id", String(userData?.employee_id));
          if (userData?.employee_name) localStorage.setItem("employee_name", String(userData?.employee_name || ''));
          if (userData?.position) localStorage.setItem("position", String(userData?.position));
          if (userData?.department) localStorage.setItem("department", String(userData?.department));
        }
      }

      // 3. Navigate only after all other logic is complete
      if (result.redirect_url) {
        if (localStorage.getItem("userRole") === "employee") {
          localStorage.setItem("dashboard", "/pending-bills");
          navigate("/pending-bills");
        } else {
          localStorage.setItem("dashboard", result.redirect_url);
          navigate(result.redirect_url);
        }
      }
    } catch (err: any) {
      let errorMessage = "Login failed. " + (err?.data?.message || err?.message);
      if (err?.data?.code === "ACTIVE_SESSION_EXISTS") {
        var isLoginAlready = window.confirm(err?.data?.message);
        if (isLoginAlready) {
          handleSubmit(event, true);
        }
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleVerifyClick = async () => {
    try {
      await verifyCaptcha({ data: { answer: captchaValue }, token: captchaResponse?.token }).unwrap();
      setIsCaptchaVerified(true);
      toast.success("captcha verified successfully");
    } catch (err) {
      let errorMessage = "An error occurred. " + (err?.data?.message || err?.message);
      toast.error(errorMessage);
    }
  };

  const getLoginTitle = () => (loginType === "vendor" ? "Login as Vendor" : "Login as Employee");
  const getIdentifierLabel = () => (loginType === "vendor" ? "Vendor Code" : "User ID");
  const getIdentifierPlaceholder = () => (loginType === "vendor" ? "Enter your vendor code" : "Enter your user ID");
  // const getAccessDescription = () => (loginType === "vendor" ? "Access your vendor account" : "Access your employee account");

  return (
    <Stack spacing={3} alignItems="center" sx={{ width: "100%", maxWidth: 600 }}>
      {/* <Typography variant="h4" component="h1" gutterBottom fontWeight="medium">
        Welcome Back
      </Typography> */}
      {/* <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Please select your login method
      </Typography> */}
      {/* 
      <FormControl component="fieldset" fullWidth>
        <RadioGroup row aria-label="login type" name="loginType" value={loginType} onChange={handleLoginTypeChange} sx={{ justifyContent: "center", mb: 2 }}>
          <FormControlLabel value="vendor" control={<Radio />} label="Login as Vendor" />
          <FormControlLabel value="employee" control={<Radio />} label="Login as Employee" />
        </RadioGroup>
      </FormControl> */}

      <Paper
        elevation={3}
        sx={{
          p: 3,
          pt: 0,
          pb: 0,
          borderRadius: 2,
          width: "100%",
          // borderTop: 3,
          // borderColor: "primary.main",
          boxShadow: 'unset'
        }}
      >
        <Stack spacing={2.5} component="form" onSubmit={handleSubmit} className="loginform">
          {/* ... (rest of your JSX is mostly fine) ... */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AccountCircleOutlinedIcon color="primary" />
            <Typography variant="h6" component="h2" fontWeight="medium">
              {getLoginTitle()}
            </Typography>
          </Stack>
          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {getAccessDescription()}
            {authMethod ? ` using ${authMethod}` : ""}.
          </Typography> */}
          {loginType === "vendor" ? (
            <>
              <TextField
                required
                fullWidth
                id="identifier"
                label={getIdentifierLabel()}
                name="identifier"
                placeholder={getIdentifierPlaceholder()}
                autoComplete="off"
                autoFocus
                value={identifier}
                onChange={handleIdentifierChange}
                InputProps={{
                  startAdornment: <VpnKeyOutlinedIcon sx={{ mr: 1, color: "action.active" }} />,
                }}
                error={isError && !identifier} // Example error highlight
                helperText={isError && !identifier ? "Identifier is required" : ""}
              />
              {identifier.length > 0 && showAuthMethodSelector && !authMethod && (
                <Box sx={{ width: "100%", textAlign: "center", my: 1 }}>
                  {/* <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Select login method:
                  </Typography> */}
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button variant="outlined" onClick={() => selectAuthMethod("password")} fullWidth startIcon={<PasswordIcon />}>
                      Password
                    </Button>
                    {/* <Button variant="outlined" onClick={() => selectAuthMethod("otp")} fullWidth startIcon={<SmsOutlinedIcon />}>
                      OTP
                    </Button> */}
                  </Stack>
                </Box>
              )}
              {authMethod === "password" && (
                <>
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      startAdornment: <PasswordIcon sx={{ mr: 1, color: "action.active" }} />,
                    }}
                    error={isError && !password}
                    helperText={isError && !password ? "Password is required" : ""}
                  />
                </>
              )}
              {authMethod === "otp" && (
                <TextField
                  required
                  fullWidth
                  name="otp"
                  label="Enter OTP"
                  type="text"
                  id="otp"
                  placeholder="Enter OTP received"
                  value={otp}
                  onChange={handleOtpChange} // Use updated handler
                  InputProps={{
                    startAdornment: <SmsOutlinedIcon sx={{ mr: 1, color: "action.active" }} />,
                  }}
                  error={isError && !otp} // Example
                  helperText={isError && !otp ? "OTP is required" : ""}
                />
              )}
              {/* Display API Error Message */}
              {isError && loginApiError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {/* Adjust based on your actual error structure */}
                  Error: {(loginApiError as any).data?.message || (loginApiError as any).error || "Login failed. Please check your credentials."}
                </Alert>
              )}
              {["password", "otp"]?.includes(authMethod) && (
                <Card variant="outlined" sx={{ mx: "auto", textAlign: "center", p: 2 }}>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    <CardContent>
                      {isCaptchaLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                          <CircularProgress />
                        </Box>
                      ) : captchaApiError ? (
                        <Alert severity="error">Failed to load captcha. Please try again.</Alert>
                      ) : captchaResponse?.image_base64 ? (
                        <img src={`data:image/png;base64,${captchaResponse.image_base64}`} alt="Captcha" style={{ width: "100px", margin: "auto" }} />
                      ) : (
                        <Typography variant="body2">No captcha available</Typography>
                      )}
                    </CardContent>
                    <TextField
                      required
                      name="captcha"
                      label="captcha"
                      type="text"
                      placeholder="Enter Captcha"
                      value={captchaValue}
                      disabled={isCaptchaVerifyLoading || isCaptchaVerified}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setCaptchaValue(event?.target?.value);
                      }}
                    />
                  </Box>
                  <CardContent sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    {!isCaptchaVerified && (
                      <Button variant="contained" size="small" onClick={handleCaptchaRefetch} sx={{ flex: 1, mt: 1 }} aria-label="Refresh Captcha">
                        Refresh <Refresh />
                      </Button>
                    )}
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      size="small"
                      disabled={!captchaValue || isCaptchaVerifyLoading || isCaptchaVerified}
                      onClick={handleVerifyClick}
                      sx={{ mt: 1, py: 1.2, flex: 1 }}
                    >
                      {isCaptchaVerifyLoading ? "Verifying..." : isCaptchaVerified ? "Verified" : "Verify"}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Display API Success Message */}
              {isSuccess && loginResponse && (
                <Alert severity="success" sx={{ width: "100%", mt: 1 }}>
                  {loginResponse.message || "Login successful!"}
                </Alert>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !identifier || !authMethod || (authMethod === "password" && !password) || (authMethod === "otp" && !otp) || !isCaptchaVerified}
                sx={{ mt: 1, py: 1.2 }}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>
              <Box sx={{ textAlign: "center", mt: 0.5, display: "flex", justifyContent: "space-between" }} style={{ gap: '40px' }}>
                {loginType === "vendor" && (
                  <Button className="action-border" variant="text" onClick={() => navigate("/forgot-password")} style={{ textTransform: "capitalize", border: '1px solid grey' }}>
                    Forgot Password / Vendor Code?
                  </Button>
                )}

                {/* <Button variant="text" onClick={() => navigate("/verify-mobile")} style={{textTransform:"capitalize"}}>
              Verify Mobile
            </Button> */}

                {loginType === "vendor" && (
                  <Button className="action-border" variant="text" onClick={() => navigate("/register")} style={{ textTransform: "capitalize", border: '1px solid grey' }}>
                    New User Registration
                  </Button>
                )}
              </Box>
              <Box sx={{ textAlign: "center", mt: 0.5, display: "flex", justifyContent: "space-between" }} style={{ gap: '40px' }}>

                <Button className="anim-text action-border" variant="text" onClick={() => window.open('https://www.cwceportal.com/bts/', '_blank')} style={{ textTransform: "capitalize" }}>
                  Old BTS Link
                </Button>
                <Button className="anim-text action-border" variant="text" onClick={openUserManual} style={{ textTransform: "capitalize" }}>
                  User Manual
                </Button>
              </Box>
              {isEnableMeetingLink && <Box className="meeting-link" onClick={() => window.open(process.env.REACT_APP_MEETING_LINK, "_blank")}>

                <a>{process.env.REACT_APP_LINK_TEXT}</a>
              </Box>}
            </>
          ) : (
            <>
              <Button type="button" fullWidth variant="contained" size="large" onClick={handleSSOLogin} sx={{ mt: 1, py: 1.2 }}>
                SSO Login
              </Button>
            </>
          )}
        </Stack>
      </Paper >
    </Stack >
  );
};

export default AuthForm;
